import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

const projectTypes = [
  {
    icon: 'ri-home-gear-line',
    title: 'Home Renovations',
    description: 'Complete home remodeling and renovation services to transform your living space.'
  },
  {
    icon: 'ri-building-2-line',
    title: 'Commercial Projects',
    description: 'Professional construction services for retail, office, and commercial properties.'
  },
  {
    icon: 'ri-add-box-line',
    title: 'Room Additions',
    description: 'Expand your home with custom room additions that blend seamlessly with existing structure.'
  },
  {
    icon: 'ri-hammer-line',
    title: 'Kitchen & Bath Remodels',
    description: 'Transform your kitchen and bathroom spaces with expert design and construction.'
  },
  {
    icon: 'ri-paint-brush-line',
    title: 'Interior Finishing',
    description: 'Professional interior finishing including drywall, painting, and trim work.'
  },
  {
    icon: 'ri-tools-fill',
    title: 'Structural Repairs',
    description: 'Expert structural repair and reinforcement to ensure safety and longevity.'
  }
];

const benefits = [
  {
    icon: 'ri-team-line',
    title: 'Full-Service Capabilities',
    description: 'From design to completion, we handle every aspect of your construction project with a single point of contact.'
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Licensed & Insured',
    description: 'Fully licensed, bonded, and insured for your protection and peace of mind throughout the project.'
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'On-Time Delivery',
    description: 'We respect your time and budget with realistic schedules and transparent communication.'
  },
  {
    icon: 'ri-award-line',
    title: 'Quality Craftsmanship',
    description: 'Decades of experience delivering superior workmanship that stands the test of time.'
  }
];

const projects = [
  {
    image: 'https://public.readdy.ai/ai/img_res/81144dd99cace8df6b285bd051fbd137.jpg',
    title: 'Modern Kitchen Renovation',
    location: 'Portland, OR'
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Complete%20home%20addition%20construction%20showing%20new%20two-story%20extension%20with%20matching%20brick%20exterior%2C%20large%20windows%2C%20and%20professional%20framing%20work%20in%20progress%20on%20residential%20property&width=800&height=600&seq=gc2&orientation=landscape',
    title: 'Two-Story Home Addition',
    location: 'Seattle, WA'
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Commercial%20retail%20space%20interior%20construction%20with%20exposed%20ceiling%2C%20modern%20lighting%20fixtures%2C%20polished%20concrete%20floors%2C%20and%20professional%20finishing%20work%20in%20progress&width=800&height=600&seq=gc3&orientation=landscape',
    title: 'Commercial Retail Build-Out',
    location: 'Eugene, OR'
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Luxury%20master%20bathroom%20remodel%20with%20freestanding%20soaking%20tub%2C%20walk-in%20glass%20shower%2C%20marble%20tile%2C%20double%20vanity%2C%20and%20elegant%20lighting%20fixtures%20in%20spa-like%20setting&width=800&height=600&seq=gc4&orientation=landscape',
    title: 'Master Bathroom Remodel',
    location: 'Bend, OR'
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Basement%20finishing%20construction%20showing%20new%20living%20space%20with%20recessed%20lighting%2C%20drywall%20installation%2C%20modern%20flooring%2C%20and%20professional%20interior%20framing%20work&width=800&height=600&seq=gc5&orientation=landscape',
    title: 'Basement Finishing',
    location: 'Salem, OR'
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Office%20building%20interior%20renovation%20with%20modern%20workspace%20design%2C%20glass%20partitions%2C%20suspended%20ceiling%2C%20professional%20lighting%2C%20and%20contemporary%20finishes&width=800&height=600&seq=gc6&orientation=landscape',
    title: 'Office Space Renovation',
    location: 'Vancouver, WA'
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Whole%20house%20renovation%20exterior%20showing%20restored%20craftsman%20home%20with%20new%20siding%2C%20updated%20windows%2C%20fresh%20paint%2C%20landscaping%2C%20and%20professional%20construction%20quality&width=800&height=600&seq=gc7&orientation=landscape',
    title: 'Whole House Renovation',
    location: 'Corvallis, OR'
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Structural%20repair%20work%20showing%20reinforced%20foundation%2C%20new%20support%20beams%2C%20professional%20framing%2C%20and%20quality%20construction%20materials%20in%20residential%20property&width=800&height=600&seq=gc8&orientation=landscape',
    title: 'Structural Reinforcement',
    location: 'Medford, OR'
  }
];

export default function GeneralConstructionPage() {
  const [scrolled, setScrolled] = useState(false);
  const [selectedProject, setSelectedProject] = useState<typeof projects[0] | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleContactClick = () => {
    navigate('/#contact');
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://readdy.ai/api/search-image?query=Professional%20construction%20site%20with%20workers%2C%20building%20materials%2C%20scaffolding%2C%20and%20modern%20construction%20equipment%20showing%20comprehensive%20general%20contracting%20services&width=1920&height=800&seq=gchero&orientation=landscape)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6">
              <span className="text-brand text-sm font-semibold uppercase tracking-wider">
                Full-Service Construction
              </span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              General Construction Services
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              From concept to completion, we deliver comprehensive construction solutions for residential and commercial projects of all sizes. Your vision, our expertise.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={handleContactClick}
                className="px-8 py-4 bg-brand text-white font-semibold rounded-lg hover:bg-brand-dark transition-colors shadow-lg whitespace-nowrap"
              >
                Request Free Estimate
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/20 whitespace-nowrap"
              >
                View All Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Project Types Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="mb-4">
              <span className="text-brand text-sm font-semibold uppercase tracking-wider">
                What We Build
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive Project Capabilities
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              No project is too big or too small. We handle everything from minor repairs to major renovations and new construction.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projectTypes.map((type, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 flex items-center justify-center mb-6">
                  <i className={`${type.icon} text-4xl text-brand`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {type.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {type.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="mb-4">
              <span className="text-brand text-sm font-semibold uppercase tracking-wider">
                Why Choose Us
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Your Trusted Construction Partner
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-brand/10 rounded-2xl">
                  <i className={`${benefit.icon} text-3xl text-brand`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Gallery */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="mb-4">
              <span className="text-brand text-sm font-semibold uppercase tracking-wider">
                Our Work
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Completed Projects
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Explore our portfolio of successful construction projects across residential and commercial sectors.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {projects.map((project, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-2xl cursor-pointer shadow-md hover:shadow-2xl transition-all duration-300"
                onClick={() => setSelectedProject(project)}
              >
                <div className="aspect-[4/3] w-full h-full">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-lg font-bold mb-2">{project.title}</h3>
                    <p className="text-sm text-gray-300 flex items-center">
                      <i className="ri-map-pin-line mr-2"></i>
                      {project.location}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-brand to-brand-dark">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-white mb-8 leading-relaxed">
            Let's discuss your construction needs and create a plan that brings your vision to life. Get your free estimate today.
          </p>
          <button
            onClick={handleContactClick}
            className="px-10 py-4 bg-white text-brand font-bold rounded-lg hover:bg-gray-100 transition-colors shadow-xl text-lg whitespace-nowrap"
          >
            Get Free Estimate
          </button>
        </div>
      </section>

      <Footer />

      {/* Lightbox Modal */}
      {selectedProject && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedProject(null)}
        >
          <button
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors"
            onClick={() => setSelectedProject(null)}
          >
            <i className="ri-close-line text-4xl"></i>
          </button>
          <div className="max-w-6xl w-full">
            <img
              src={selectedProject.image}
              alt={selectedProject.title}
              className="w-full h-auto rounded-lg"
            />
            <div className="mt-6 text-center">
              <h3 className="text-2xl font-bold text-white mb-2">
                {selectedProject.title}
              </h3>
              <p className="text-gray-400 flex items-center justify-center">
                <i className="ri-map-pin-line mr-2"></i>
                {selectedProject.location}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
